<script lang="ts" setup>
import BaseCard from "@/components/BaseCard.vue";
import BaseContent from "@/components/BaseContent.vue";
import BasePageHeading from "@/components/BasePageHeading.vue";
import BasePill from "@/components/BasePill.vue";
import BaseSubContent from "@/components/BaseSubContent.vue";
import CharacterStat from "@/components/CharacterStat.vue";
import CharacterSuperAtkCategory from "@/components/CharacterSuperAtkCategory.vue";
import CharacterThumbnail from "@/components/CharacterThumbnail.vue";
import TheCommentsCard from "@/components/TheCommentsCard.vue";
import TheLayout from "@/components/TheLayout.vue";
import { PaginatedCommentData } from "@/types";
import { Stage } from "@/types/stage";
import { usePage } from "@inertiajs/vue3";

const page = usePage();
defineProps<{
  stage: Stage;
  comments: PaginatedCommentData;
}>();

function superAttackDamage(enemy: StageEnemy, superAtk: SuperAtk): string {
  let dmg = enemy.atk;
  let increase = 1;

  superAtk.effects.forEach((effect) => {
    if (
      (effect.type === 1 || effect.type === 3) &&
      (effect.target === 1 || effect.target === 2) &&
      (effect.calculation === 0 || effect.calculation === 2) &&
      effect.values[0] > 0
    ) {
      increase += effect.values[0] / 100;
    }
  });

  increase += superAtk.atk_multiplier / 100;

  return (dmg * increase).toLocaleString();
}

function territorySkillEffectCalculation(effect: TerritorySkillEffect): string {
  if (effect.calculation !== 2) {
    return "";
  }

  let calculation = "";

  if (effect.values[0] > 0 && effect.values[1] > 0) {
    calculation = `ATK +${effect.values[0]}%/DEF +${effect.values[1]}%`;
  } else if (effect.values[0] > 0) {
    calculation = `+${effect.values[0]}%`;
  } else if (effect.values[1] > 0) {
    calculation = `+${effect.values[1]}%`;
  }

  return `(${calculation})`;
}
</script>

<template>
  <TheLayout
    :description="`Details and enemy information for DRAGON BALL Z Dokkan Battle stage ${stage.quest.area.name}: ${stage.quest.name} on difficulty
         ${stage.difficulty}.`"
    :head-image="`${page.props.app.cdn_url}/assets/en/${
      stage.quest.area.images['button'] ?? stage.quest.area.images['header']
    }`"
    :heading-href="route('stages.index')"
    :title="`${stage.quest.name} - ${stage.difficulty}`"
    heading="Stages"
  >
    <div class="flex flex-col items-center justify-between gap-2 lg:flex-row">
      <div class="grow-0 px-4 sm:px-0 lg:order-2">
        <img
          v-if="stage.quest.area.images['banner'] !== null"
          :alt="stage.quest.name"
          :src="`${page.props.app.cdn_url}/assets/en/${stage.quest.area.images['banner']}`"
          class="max-h-16 w-auto"
        />
      </div>

      <BasePageHeading>
        {{ stage.quest.name }}
      </BasePageHeading>
    </div>

    <BaseCard>
      <template #heading>{{ stage.difficulty }}</template>

      <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <CharacterStat type="Stamina">
          {{ stage.stamina }}
        </CharacterStat>

        <CharacterStat type="Rank Exp.">
          {{ stage.rank_exp }}
        </CharacterStat>

        <CharacterStat type="Zeni">
          {{ stage.zeni }}
        </CharacterStat>

        <CharacterStat type="Link Lv. Up Rate">
          {{ stage.link_skill_lv_up_probability * 100 }}%
        </CharacterStat>
      </div>
    </BaseCard>

    <div v-if="stage.enemies.length > 0" class="flex flex-col gap-4">
      <BasePageHeading>Enemies</BasePageHeading>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        <BaseCard v-for="enemy in stage.enemies">
          <template #heading>{{ enemy.character.name }}</template>

          <div class="flex items-center gap-4">
            <CharacterThumbnail
              :character="enemy.character"
              class="mr-2 mt-2"
            />

            <div class="flex w-full flex-col gap-2">
              <div class="flex flex-row flex-wrap justify-between gap-2">
                <CharacterStat :value="enemy.hp" type="HP" />
                <CharacterStat :value="enemy.atk" type="ATK" />
                <CharacterStat :value="enemy.def" type="DEF" />
                <CharacterStat type="Actions/Turn"
                  >{{ enemy.turn_attacks }}
                </CharacterStat>
              </div>

              <p class="flex gap-1 text-xs text-green-100">
                <i-fa6-solid-chess-king />
                <span
                  >Encountered on map
                  <span class="font-medium">tile {{ enemy.tile + 1 }}</span
                  >,
                  <span class="font-medium"
                    >battle {{ enemy.battle + 1 }}</span
                  ></span
                >
              </p>
            </div>
          </div>

          <BaseContent
            v-for="superAtk in enemy.character.super_atks"
            v-if="enemy.character.super_atks.length > 0"
          >
            <template #header>
              {{ superAtk.name }}
              <CharacterSuperAtkCategory :category="superAtk.category" />
            </template>
            {{ superAtk.description }}

            <CharacterStat type="Damage">
              {{ superAttackDamage(enemy, superAtk) }}
            </CharacterStat>
          </BaseContent>

          <BaseContent v-if="enemy.skills.length > 0" header="Skills">
            <ul class="ml-4 list-disc">
              <li v-for="skill in enemy.skills">
                {{ skill.description ?? skill.name }}
                <span v-if="skill.type === 7 || skill.type === 8"
                  >(<span class="font-medium text-green-100"
                    >{{ skill.values[0] }}%</span
                  >)</span
                ><span v-if="skill.type === 15"
                  >(<span class="font-medium text-green-100"
                    >{{ skill.values[1] }}%</span
                  >)</span
                >
              </li>
            </ul>
          </BaseContent>

          <BaseContent v-if="enemy.territory_skill">
            <template #header> Territory Skill</template>
            <ul class="ml-4 list-disc">
              <li v-for="effect in enemy.territory_skill.effects">
                {{ effect.name }}<br />
                <span class="text-sm"
                  >{{ effect.description }}
                  {{ territorySkillEffectCalculation(effect) }}</span
                >
              </li>
            </ul>

            <BaseSubContent header="Cancel Condition">
              {{ enemy.territory_skill.cancel_condition }}
            </BaseSubContent>
          </BaseContent>

          <BaseContent v-if="enemy.ai.length > 0" header="Actions">
            <div class="grid grid-cols-2 gap-2">
              <div v-for="(ai, turn) in enemy.ai">
                <span
                  v-if="enemy.ai.length > 1"
                  class="font-semibold text-green-100"
                  >{{
                    turn + 1 !== enemy.ai.length ||
                    (turn + 1 === enemy.ai.length && ai.length === 1)
                      ? turn + 1 === enemy.ai.length &&
                        ai.length === 1 &&
                        ai.next_ai_type === null
                        ? "Consequent Actions"
                        : `Action ${turn + 1}`
                      : "Consequent Turns"
                  }}</span
                >

                <div v-for="action in ai">
                  <span class="flex items-center gap-1 font-medium">
                    {{ action.action_type
                    }}<BasePill v-if="action.weight < 100 && ai.length > 1"
                      >Weight {{ action.weight }}
                    </BasePill></span
                  >
                  <ul class="ml-4 list-disc text-sm">
                    <li v-if="action.hp_recovery_rate > 0">
                      Recovers
                      {{ action.hp_recovery_rate }}% HP
                    </li>
                    <li v-if="ai.length > 1">
                      Max.
                      {{ action.max_per_turn }} time{{
                        action.max_per_turn !== 1 ? "s" : ""
                      }}
                      per turn
                    </li>
                    <li v-if="action.from_hp > 0 || action.to_hp < 100">
                      Between {{ action.from_hp }}% &ndash; {{ action.to_hp }}%
                      HP
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </BaseContent>
        </BaseCard>
      </div>
    </div>

    <TheCommentsCard
      :commentable-id="stage.id"
      :comments="comments"
      commentable-type="App\Models\Dokkan\SugorokuMap"
    />
  </TheLayout>
</template>

<style scoped></style>
